// This file is used to register and set up the core modules that we need for pages and embedded components

import "core-js/stable";
import "regenerator-runtime/runtime";
import "bootstrap";
import { createStore } from "vuex";
import { createApp } from "vue";
import { createPinia } from "pinia";
import { AwsRum } from "aws-rum-web";

// include and register plugins
import id from "Util/uniqueIdGenerator.js";
import { IS_DEBUG_BUILD } from "Util/util";
import i18n from "Util/i18n";
import { createI18nVueModule } from "Util/localeUtil";

// register global vue components
import appLayout from "Components/page/pc-app-layout.vue";
import tab from "Components/pc-tab.vue";
import tabs from "Components/pc-tabs.vue";
import section from "Components/pc-section.vue";
import modal from "Components/pc-modal.vue";
import dialog from "Components/pc-dialog.vue";
import inputGroup from "Components/pc-input-group.vue";
import input from "Components/pc-input.vue";
import textarea from "Components/pc-textarea.vue";
import select from "Components/pc-select.vue";
import checkbox from "Components/pc-checkbox.vue";
import checkboxSet from "Components/pc-checkbox-set.vue";
import datetime from "Components/pc-datetime.vue";
import radio from "Components/pc-radio-group.vue";
import radioScale from "Components/pc-tile-group.vue";
import search from "Components/search/pc-search.vue";
import date from "Components/pc-date.vue";
import table from "Components/table/pc-table.vue";
import loader from "Components/pc-loader.vue";
import button from "Components/pc-button.vue";
import buttonLink from "Components/pc-button-link.vue";
import buttonIcon from "Components/pc-button-icon.vue";
import alert from "Components/pc-alert.vue";
import file from "Components/pc-file.vue";
import capsule from "Components/pc-capsule.vue";
import transition from "Components/pc-transition.vue";
import label from "Components/pc-label.vue";
import helpicon from "Components/pc-help-icon.vue";
import copyable from "Components/pc-copyable.vue";
import popper from "Components/pc-popper.vue";
import viewport from "Components/pc-viewport.vue";
import filterlist from "Components/filterableList/pc-filter-list.vue";
import interact from "Components/pc-interact.vue";
import orderable from "Components/orderable/pc-orderable.vue";
import expandList from "Components/pc-expand-list.vue";
import sensitiveIcon from "Components/pc-sensitive-icon.vue";
import formattedNumber from "Components/pc-formatted-number.vue";
import panelSlider from "Components/pc-panel-slider.vue";
import multilineDisplay from "Components/pc-multiline-display.vue";
import categorizedFilter from "Components/categorized/pc-categorized-filter.vue";
import categorizedDataFields from "Components/categorized/pc-categorized-datafields.vue";
import bsIcon from "Components/pc-bs-icon.vue";
import validationError from "Components/pc-validation-error.vue";
import emptyPlaceholder from "Components/pc-empty-placeholder.vue";
import buttonRow from "Components/layout/pc-button-row.vue";
import tempSpace from "Components/layout/pc-temp-space.vue";
import slider from "Components/pc-slider.vue";
import filteredSelect from "Components/filteredSelect/pc-filtered-select.vue";

import { VUE_I18N_SYMBOL, VUE_ROOT_APP_SYMBOL, LAYOUTS_SYMBOL, AWS_RUM_SYMBOL } from "./injectionSymbols";

export function createAppWithCoreComponents(component, componentModel, pcAppFactory) {
    const vueRootApp = createApp(component, componentModel);
    const pinia = createPinia();
    vueRootApp.use(pinia);
    const vueI18n = createI18nVueModule();
    vueRootApp.use(vueI18n); // creates the vue-i18n plugin
    vueRootApp.use(id);
    vueRootApp.use(i18n); // this is our own wrapper to make using vue-i18n easier
    vueRootApp.use(
        createStore({
            strict: IS_DEBUG_BUILD
        })
    );

    // there are two checks to see if we should enable client side metrics
    // 1. if the instance is eligible for client side metrics
    // 2. if so, if the instance has client side metrics enabled
    if (componentModel.AreClientSideMetricsAvailable) {
        if (componentModel.EnableClientSideMetrics) {
            // if we can't get either secret, then do not init the AWS RUM client
            if (
                componentModel.ClientSideMetricsModel.AwsRumConfig &&
                componentModel.ClientSideMetricsModel.AwsRumApplicationId
            ) {
                try {
                    const APPLICATION_ID = componentModel.ClientSideMetricsModel.AwsRumApplicationId;
                    const APPLICATION_VERSION = "1.0.0";
                    const APPLICATION_REGION = "us-west-2";
                    const config = componentModel.ClientSideMetricsModel.AwsRumConfig;

                    const awsRum = new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
                    vueRootApp.provide(AWS_RUM_SYMBOL, awsRum);
                } catch (error) {
                    // Ignore errors thrown during CloudWatch RUM web client initialization
                }
            }
        }
    }

    vueRootApp.provide(VUE_I18N_SYMBOL, vueI18n);
    vueRootApp.provide(VUE_ROOT_APP_SYMBOL, vueRootApp);
    vueRootApp.provide(LAYOUTS_SYMBOL, { app: null, page: null });

    vueRootApp.component("pc-app-layout", appLayout);
    // vueRootApp.component("pc-page-header", pageHeader);
    vueRootApp.component("pc-tab", tab);
    vueRootApp.component("pc-tabs", tabs);
    vueRootApp.component("pc-section", section);
    vueRootApp.component("pc-modal", modal);
    vueRootApp.component("pc-dialog", dialog);
    vueRootApp.component("pc-input-group", inputGroup);
    vueRootApp.component("pc-input", input);
    vueRootApp.component("pc-textarea", textarea);
    vueRootApp.component("pc-select", select);
    vueRootApp.component("pc-checkbox", checkbox);
    vueRootApp.component("pc-checkbox-set", checkboxSet);
    vueRootApp.component("pc-datetime", datetime);
    vueRootApp.component("pc-radio-group", radio);
    vueRootApp.component("pc-tile-group", radioScale);
    vueRootApp.component("pc-search", search);
    vueRootApp.component("pc-date", date);
    vueRootApp.component("pc-table", table);
    vueRootApp.component("pc-loader", loader);
    vueRootApp.component("pc-button", button);
    vueRootApp.component("pc-button-link", buttonLink);
    vueRootApp.component("pc-button-icon", buttonIcon);
    vueRootApp.component("pc-alert", alert);
    vueRootApp.component("pc-file", file);
    vueRootApp.component("pc-capsule", capsule);
    vueRootApp.component("pc-transition", transition);
    vueRootApp.component("pc-label", label);
    vueRootApp.component("pc-help-icon", helpicon);
    vueRootApp.component("pc-copyable", copyable);
    vueRootApp.component("pc-popper", popper);
    vueRootApp.component("pc-viewport", viewport);
    vueRootApp.component("pc-filter-list", filterlist);
    vueRootApp.component("pc-interact", interact);
    vueRootApp.component("pc-orderable", orderable);
    vueRootApp.component("pc-expand-list", expandList);
    vueRootApp.component("pc-sensitive-icon", sensitiveIcon);
    vueRootApp.component("pc-formatted-number", formattedNumber);
    vueRootApp.component("pc-panel-slider", panelSlider);
    vueRootApp.component("pc-multiline-display", multilineDisplay);
    vueRootApp.component("pc-categorized-filter", categorizedFilter);
    vueRootApp.component("pc-categorized-datafields", categorizedDataFields);
    vueRootApp.component("pc-bs-icon", bsIcon);
    vueRootApp.component("pc-validation-error", validationError);
    vueRootApp.component("pc-empty-placeholder", emptyPlaceholder);
    vueRootApp.component("pc-button-row", buttonRow);
    vueRootApp.component("pc-temp-space", tempSpace);
    vueRootApp.component("pc-slider", slider);
    vueRootApp.component("pc-filtered-select", filteredSelect);

    vueRootApp.config.globalProperties.$serviceControllers = {};

    const $app = pcAppFactory(vueRootApp, componentModel);
    vueRootApp.config.globalProperties.$app = $app;

    return vueRootApp;
}

//polyfill CustomEvent if not found
if (window) {
    if (typeof window.CustomEvent !== "function") {
        const CustomEvent = function CustomEvent(event, params) {
            params = params || { bubbles: false, cancelable: false, detail: null };
            const evt = document.createEvent("CustomEvent");
            evt.initCustomEvent(event, params.bubbles, params.cancelable, params.detail);
            return evt;
        };

        window.CustomEvent = CustomEvent;
    }
}
